import { LoadingSpinner } from '@/shared/ui';
import { Button, Text } from '@noorden-insurtech/alba-design-system';
import { useNavigate } from '@tanstack/react-router';
import { Ban } from 'lucide-react';
import { type ReactNode, useRef } from 'react';
import type { BrokerWriteGuard } from '../utils/broker-status.util';

type BrokerWriteGateProps = {
  guard: BrokerWriteGuard;
  /** Destino do botão "Voltar" quando a escrita está travada. */
  backTo: '/quotes' | '/proposals';
  backLabel?: string;
  children: ReactNode;
};

/**
 * Tela de criar/editar acessada direto pela URL: com a corretora inativa mostra
 * o motivo e um "Voltar" no lugar do formulário. A Api continua barrando.
 *
 * Depois de liberado uma vez, uma nova consulta sem resultado ainda (ex.: troca
 * de corretora no header) não desmonta o formulário; só o motivo o substitui.
 */
export function BrokerWriteGate({
  guard,
  backTo,
  backLabel = 'Voltar',
  children,
}: BrokerWriteGateProps) {
  const navigate = useNavigate();
  const wasReleasedRef = useRef(false);

  if (!guard.blocked) wasReleasedRef.current = true;

  if (!guard.blocked || (!guard.reason && wasReleasedRef.current)) {
    return <>{children}</>;
  }

  if (!guard.reason) return <LoadingSpinner />;

  return (
    <div className='mx-auto mt-10 flex max-w-lg flex-col items-center rounded-xl border border-alba-gray-200 bg-alba-gray-50 px-8 py-16 text-center'>
      <Ban className='mb-4 h-16 w-16 text-alba-gray-400' />
      <Text className='mb-6 text-sm text-alba-error-red-500'>
        {guard.reason}
      </Text>
      <Button
        variant='outline'
        size='md'
        onClick={() => navigate({ to: backTo })}
      >
        {backLabel}
      </Button>
    </div>
  );
}
